<template>
  <result-item-template
    :name="result.item.name"
    :icon-glyph="result.item.onxType ?? undefined"
    :image-url="result.item.photos?.[0]?.contentUrl ?? undefined"
  >
    <template #metadata>
      <ys-text
        v-if="hasMetadata"
        type="metadata1"
        class="result-details-subtext"
        data-test="search-result-subtext"
      >
        <span v-if="cityState && result.item.onxType !== 'GEOCODE'">
          {{ cityState }}
        </span>
        <!-- Distance away from user -->
        <span v-if="distanceInKilometers">
          <span
            v-if="cityState && result.item.onxType !== 'GEOCODE'"
            class="divider"
          >
            |
          </span>
          <x-distance
            from="kilometers"
            label-type="full"
            :length="distanceInKilometers"
          />
          away
        </span>
        <!-- Acreage of result item -->
        <span v-if="result.item.area" class="divider">|</span>
        <span v-if="result.item.area" data-test="acres-text">
          <x-area
            :area="result.item.area"
            :from="areaFromUnit"
            :to="areaToUnit"
            :maximum-fraction-digits="digitsPerApp"
            label-type="full"
          />
        </span>
      </ys-text>
    </template>
  </result-item-template>
</template>

<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import {
  getCityState,
  getDistanceInKilometers,
} from '@/lib/search/search-utils'
import { searchMode } from '@/services/search-service'
import { SearchMode } from '@/services/search-service/constants'
import { getItem } from '@/plugins/storage'

import resultItemTemplate from '@/components/search/result-items/result-item-template.vue'
import '@onxmaps/yellowstone-ui/ys-text'
import xDistance from '@/components/shared/x-distance.vue'
import xArea from '@/components/x-area.vue'

import type { SearchResultProps } from '@/services/search-service/types'

///// props/emits /////
interface GenericResultItemProps {
  result: SearchResultProps
}
const { result } = defineProps<GenericResultItemProps>()

///// computed /////
const isLocationMode = computed(() => searchMode() === SearchMode.Location)

const isImperial = computed(() => getItem('unitType') === 'imperial')

const hasMetadata = computed(() => {
  return !!cityState.value || !!distanceInKilometers.value || !!result.item.area
})

// Fish area unit defaults to sqMeters, Hunt is acres
const areaFromUnit = computed(() => {
  return isLocationMode.value ? 'sqMeters' : 'acres'
})
const areaToUnit = computed(() => (isImperial.value ? 'acres' : 'hectares'))

const digitsPerApp = computed(() => {
  return isLocationMode.value ? 0 : 2
})

const cityState = computed(() => getCityState(result))

const distanceInKilometers = computed(() => {
  const point = result.item.point
  return point ? getDistanceInKilometers(point) : undefined
})
</script>

<style lang="scss" scoped>
.divider {
  padding: 0 var(--ys-spacing-25);
}
</style>
